import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { AnalogClock } from './AnalogClock';
import { Mail, Linkedin, Github, Instagram, MapPin, Briefcase, Cake, Sparkles } from 'lucide-react';
export function BentoGrid() {
  const {
    ref,
    isVisible
  } = useScrollReveal(0.1);
  return <section className="py-20 px-6" data-id="element-38">
      <div ref={ref} className={`max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-39">
        {/* 1. Large Card: Bio (col-span-2 row-span-2) */}
        <div className="md:col-span-2 md:row-span-2 bg-white/5 border border-white/10 rounded-3xl p-8 hover:border-white/20 transition-colors relative overflow-hidden group" data-id="element-40">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-41" />
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-42">
            A Little About Me{' '}
            <span className="animate-wave inline-block origin-bottom-right hover:animate-spin" data-id="element-43">
              👋
            </span>
          </h2>
          <div className="space-y-4 text-gray-300 leading-relaxed text-lg" data-id="element-44">
            <p data-id="element-45">
              I'm a builder by trade and a creator by heart. From my early days
              as an architect to my current role leading product design in the
              financial services space, I've spent my career obsessing over how
              people move through spaces—both physical and digital. I don't just
              'make things look good'; I build systems that work.
            </p>
            <p data-id="element-46">
              My approach combines data-driven insights with creative
              problem-solving to build products that not only work beautifully
              but also drive real business results. When I'm not at my desk, I'm
              usually elbows-deep in a home renovation project, wandering
              through a local gallery, or crafting an over-the-top costume for
              my next big event.
            </p>
          </div>
        </div>

        {/* 2. Medium Card: Profile Photo (col-span-2 row-span-1) */}
        <div className="md:col-span-2 md:row-span-1 relative rounded-3xl overflow-hidden group h-[300px] border border-white/10" data-id="element-47">
          <img src="https://i.imgur.com/aH8MlNh.jpeg" alt="Profile" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105" data-id="element-48" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-49">
            <span className="text-white font-bold text-xl" data-id="element-50">Taylor Sams</span>
          </div>
        </div>

        {/* 3. Small Card: Local Time (col-span-1 row-span-1) */}
        <div className="md:col-span-1 md:row-span-1 bg-white/5 border border-white/10 rounded-3xl hover:border-white/20 transition-colors" data-id="element-51">
          <AnalogClock data-id="element-52" />
        </div>

        {/* 4. Card: Business Card Style (col-span-1 row-span-2) */}
        <div className="md:col-span-1 md:row-span-2 bg-white text-black rounded-3xl p-8 flex flex-col relative overflow-hidden group hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-shadow duration-500" data-id="element-53">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-54" />
          <div className="space-y-1 mb-6" data-id="element-55">
            <div className="w-12 h-12 bg-black rounded-full mb-6 flex items-center justify-center text-white font-bold text-xl" data-id="element-56">
              TS
            </div>
            <h3 className="text-2xl font-bold tracking-tight" data-id="element-57">Taylor Sams</h3>
            <p className="text-gray-600 font-medium" data-id="element-58">Product Designer</p>
          </div>
          <div className="space-y-4 text-sm font-medium" data-id="element-59">
            <div className="flex items-center gap-3 text-gray-600" data-id="element-60">
              <Mail size={16} className="flex-shrink-0" data-id="element-61" />
              <span data-id="element-62">taylorjsams@gmail.com</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600" data-id="element-63">
              <MapPin size={16} className="flex-shrink-0" data-id="element-64" />
              <span data-id="element-65">New York, NY</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600" data-id="element-66">
              <Cake size={16} className="flex-shrink-0" data-id="element-67" />
              <span data-id="element-68">October 10</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600" data-id="element-69">
              <Sparkles size={16} className="flex-shrink-0" data-id="element-70" />
              <span data-id="element-71">Clear & Sparkly</span>
            </div>
          </div>
        </div>

        {/* 5. Card: Connect (col-span-1 row-span-1) */}
        <div className="md:col-span-1 md:row-span-1 bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-col justify-center hover:border-white/20 transition-colors" data-id="element-72">
          <h3 className="text-lg font-bold text-white mb-6 text-center" data-id="element-73">
            Connect with Me
          </h3>
          <div className="flex justify-center gap-4" data-id="element-74">
            {[{
            icon: Linkedin,
            href: 'https://www.linkedin.com/in/taylor-sams/',
            label: 'LinkedIn'
          }, {
            icon: Instagram,
            href: 'https://www.instagram.com/taylorjsams/',
            label: 'Instagram'
          }].map((Social, i) => <a key={i} href={Social.href} aria-label={Social.label} className="flex items-center justify-center w-14 h-14 bg-white/5 border border-white/10 rounded-2xl hover:bg-white hover:text-black transition-all duration-300 group" data-id="element-75">
                <Social.icon size={24} className="text-gray-400 group-hover:text-black transition-colors" data-id="element-76" />
              </a>)}
          </div>
        </div>
      </div>
    </section>;
}